import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";

const WarehousesForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    address: "",
  });

  const [loading, setLoading] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      if (isEdit) {
        const res = await axios.get(`${API}/warehouses/${id}`, getAuthHeader());
        const data = res.data;
        setFormData({
          name: data.name || "",
          address: data.address || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch warehouse data", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!formData.name) {
      alert("Please fill in the warehouse name.");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this data?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const rawPayload = {
        ...formData,
        tenantId: user.tenantId,
        createdByUserId: user.id,
      };

      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_, v]) => v !== "")
      );

      if (isEdit) {
        await axios.put(`${API}/warehouses/${id}`, payload, getAuthHeader());
        alert("Warehouse updated!");
      } else {
        await axios.post(`${API}/warehouses`, payload, getAuthHeader());
        alert("Warehouse created!");
      }

      navigate("/warehouse");
    } catch (error) {
      console.error("Failed to save warehouse:", error);
      alert("Failed to save warehouse");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this warehouse?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/warehouses/${id}`, getAuthHeader());
      alert("Warehouse deleted");
      navigate("/warehouse");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete warehouse");
    }
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Warehouse" : "Add Warehouse"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="warehouse-form"
              className="bg-blue-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/warehouse")}
              className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-[13px] flex items-center gap-1"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1"
                disabled={loading}
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="warehouse-form" onSubmit={handleSubmit}>
		  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div className="flex flex-col gap-2">
			  <label className="text-[13px] font-medium">
				Warehouse Name <span className="text-red-500">*</span>
			  </label>
			  <input
				type="text"
				name="name"
				value={formData.name}
				onChange={handleChange}
				className="border rounded px-2 py-1 text-[13px]"
				required
			  />
			</div>

			<div className="flex flex-col gap-2">
			  <label className="text-[13px] font-medium">Address</label>
			  <textarea
				name="address"
				value={formData.address}
				onChange={handleChange}
				className="border rounded px-2 py-1 text-[13px]"
				rows={3}
			  />
			</div>
		  </div>
		</form>

      </div>
    </div>
  );
};

export default WarehousesForm;
